import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, BadgeCheck, UserCheck, ArrowUpRight, Home as HomeIcon,
  Building2, UtensilsCrossed, CalendarCheck, Sparkles, Sofa, Star, MapPin,
} from "lucide-react";
import { Reveal, MaskLines } from "../components/Motion";
import { BRAND } from "../lib/api";

const IMG = {
  residential: "https://images.pexels.com/photos/7174386/pexels-photo-7174386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  commercial: "https://images.pexels.com/photos/36631699/pexels-photo-36631699.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  restaurant: "https://images.pexels.com/photos/37532766/pexels-photo-37532766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  team: "https://images.pexels.com/photos/9462636/pexels-photo-9462636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const trust = [
  { icon: ShieldCheck, title: "Insured & Bonded", sub: "Full liability coverage on every job." },
  { icon: BadgeCheck, title: "Satisfaction Guaranteed", sub: "We will re-clean free if you're not happy." },
  { icon: UserCheck, title: "Background-Checked Staff", sub: "Vetted, trained, trusted with your keys." },
];

const services = [
  { icon: HomeIcon, tag: "Residential", title: "Homes & Condos", desc: "Standard, deep and move-in/out cleans priced by home size — flat, transparent rates.", img: IMG.residential },
  { icon: Building2, tag: "Commercial", title: "Offices & Schools", desc: "Recurring janitorial for workspaces that clients and staff walk into every day.", img: IMG.commercial },
  { icon: UtensilsCrossed, tag: "Restaurant", title: "Kitchens & Dining", desc: "Degreasing and back-of-house cleaning to health-code standard — custom quotes.", img: IMG.restaurant },
];

const steps = [
  { n: "01", icon: CalendarCheck, title: "Book", desc: "Pick your service and time in under two minutes — instant online booking, no phone tag." },
  { n: "02", icon: Sparkles, title: "We Clean", desc: "Our insured, background-checked crew arrives on schedule with pro-grade equipment." },
  { n: "03", icon: Sofa, title: "You Relax", desc: "Walk into a spotless space. Not happy with a spot? We come back and fix it free." },
];

const testimonials = [
  { q: "Our office has never looked sharper. Clients notice the moment they walk in.", n: "Priya M.", r: "Office Manager, North York" },
  { q: "They handled our restaurant's kitchen degreasing better than any vendor we've used.", n: "Marco D.", r: "Owner, Toronto Bistro" },
  { q: "Reliable, on time, and my condo sparkles every single visit. Worth every dollar.", n: "Jason L.", r: "Homeowner, Mississauga" },
];

export default function Home() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="absolute inset-0 bg-secondary grain" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">
                <span className="h-px w-8 bg-tide" /> Greater Toronto Area
              </span>
            </Reveal>
            <h1 className="mt-6 font-heading font-extrabold tracking-tighter text-ink text-5xl md:text-7xl leading-[1.02]">
              <MaskLines lines={["Spotless spaces.", "Healthier places.", ""]} />
              <span className="block overflow-hidden">
                <motion.span
                  className="block text-tide"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.36, ease: [0.22, 1, 0.36, 1] }}
                >
                  Better impressions.
                </motion.span>
              </span>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-7 text-ink/65 text-lg max-w-xl leading-relaxed">
                Professional cleaning you'd trust with your office keys — for offices, schools,
                restaurants and homes. Insured, bonded and background-checked.
              </p>
            </Reveal>
            <Reveal delay={0.62}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/book" data-testid="hero-quote-btn" className="group inline-flex items-center gap-2 rounded-sm bg-ink text-white font-semibold px-7 py-4 transition-transform hover:-translate-y-1 hover:bg-brand">
                  Get a Free Quote <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link to="/services" data-testid="hero-services-btn" className="inline-flex items-center gap-2 rounded-sm border border-ink/20 text-ink font-semibold px-7 py-4 transition-colors hover:border-tide hover:text-tide">
                  View Pricing
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.74}>
              <div className="mt-10 flex items-center gap-6 text-sm text-ink/60">
                <div className="flex items-center gap-1 text-tide">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <span>Trusted across 7 GTA cities</span>
              </div>
            </Reveal>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative clip-arch overflow-hidden ring-1 ring-border shadow-[0_30px_80px_-30px_rgba(4,27,36,0.4)]">
              <motion.img
                src={IMG.residential}
                alt="Pristine cleaned interior"
                className="w-full h-[420px] md:h-[560px] object-cover"
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/30 to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-sm border border-border shadow-xl px-6 py-4 flex items-center gap-3"
            >
              <div className="h-11 w-11 rounded-sm bg-tide/10 flex items-center justify-center text-tide"><ShieldCheck size={22} /></div>
              <div>
                <div className="font-heading font-bold text-ink leading-tight">100% Insured</div>
                <div className="text-xs text-ink/55">& Bonded Crews</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 md:py-10 border-y border-border bg-ink overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {["Residential", "Commercial", "Restaurant", "Offices", "Schools", "Move-In / Out"].map((t) => (
                <span key={t} className="mx-8 md:mx-12 text-4xl md:text-6xl font-heading font-extrabold tracking-tight text-white/[0.09] uppercase">
                  {t} <span className="text-tide-bright/20">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-6">
          {trust.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.1}>
              <div data-testid={`trust-${i}`} className="group h-full rounded-sm border border-border bg-white p-8 transition-shadow hover:shadow-[0_20px_50px_-25px_rgba(4,27,36,0.35)]">
                <div className="h-12 w-12 rounded-sm bg-tide/10 text-tide flex items-center justify-center transition-colors group-hover:bg-tide group-hover:text-white">
                  <t.icon size={24} />
                </div>
                <h3 className="mt-6 font-heading font-bold text-xl text-ink">{t.title}</h3>
                <p className="mt-2 text-ink/60 leading-relaxed">{t.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-secondary grain relative">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <Reveal>
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">01 — What We Clean</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-4xl md:text-5xl text-ink max-w-2xl">
              One trusted crew for every space you run.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div data-testid={`service-card-${i}`} className="group h-full rounded-sm border border-border bg-white overflow-hidden transition-shadow hover:shadow-[0_25px_60px_-30px_rgba(4,27,36,0.4)]">
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-sm bg-white/95 text-ink text-xs font-bold uppercase tracking-wide px-3 py-1.5">
                      <s.icon size={14} className="text-tide" /> {s.tag}
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-heading font-bold text-xl text-ink">{s.title}</h3>
                    <p className="mt-2 text-ink/60 leading-relaxed">{s.desc}</p>
                    <Link to="/services" className="mt-5 inline-flex items-center gap-1 text-tide font-semibold text-sm hover:gap-2 transition-all">
                      See pricing <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal>
          <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">02 — The Process</span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-4xl md:text-5xl text-ink">Book. We clean. You relax.</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="relative rounded-sm border border-border bg-white p-8 h-full">
                <span className="font-mono text-5xl font-bold text-tide/15">{s.n}</span>
                <div className="mt-4 h-12 w-12 rounded-sm bg-ink text-white flex items-center justify-center"><s.icon size={22} /></div>
                <h3 className="mt-5 font-heading font-bold text-2xl text-ink">{s.title}</h3>
                <p className="mt-2 text-ink/60 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-ink text-white grain relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <Reveal>
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide-bright">03 — What Clients Say</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-4xl md:text-5xl">Trusted with keys across the GTA.</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`testimonial-${i}`} className="h-full rounded-sm border border-white/10 bg-white/[0.03] p-8">
                  <div className="flex gap-1 text-tide-bright mb-5">
                    {[...Array(5)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}
                  </div>
                  <p className="text-lg leading-relaxed text-white/85">"{t.q}"</p>
                  <div className="mt-6 pt-5 border-t border-white/10">
                    <div className="font-heading font-bold">{t.n}</div>
                    <div className="text-sm text-white/50">{t.r}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">04 — Where We Work</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-4xl md:text-5xl text-ink">Serving the Greater Toronto Area.</h2>
            <p className="mt-5 text-ink/60 text-lg leading-relaxed max-w-lg">
              From downtown towers to suburban homes, our crews cover the GTA's core cities.
              Not sure if we reach you? Ask — we're expanding.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {BRAND.areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-2 rounded-sm border border-border bg-white px-4 py-2.5 text-sm font-medium text-ink">
                  <MapPin size={14} className="text-tide" /> {a}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-sm overflow-hidden border border-border shadow-[0_25px_60px_-35px_rgba(4,27,36,0.5)]">
              <iframe
                title="GTA Service Area"
                data-testid="service-area-map"
                src="https://www.google.com/maps?q=Toronto,Ontario&z=9&output=embed"
                className="w-full h-[420px] grayscale-[35%]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-tide relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24 grid md:grid-cols-[1.5fr_1fr] gap-10 items-center">
          <div>
            <h2 className="font-heading font-extrabold tracking-tight text-4xl md:text-5xl text-white">Ready for a spotless space?</h2>
            <p className="mt-4 text-white/85 text-lg max-w-xl">Get a free, no-obligation quote today. Final price confirmed after a free walkthrough.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/book" data-testid="cta-book-btn" className="inline-flex items-center gap-2 rounded-sm bg-white text-ink font-bold px-8 py-4 transition-transform hover:-translate-y-1">
              Book Now <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
